import { FC, useState } from 'react';

import { Box, Grid, Stack, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import { ControlledCheckBox } from 'src/components/CheckBox/ControlledCheckBox';
import { InputBaseField } from 'src/components/common/form/FormInput';
import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { InputWithSearch } from 'src/components/InputWithSearch';

import { Card } from '../card';

interface Step2AddressInformation {
    formik: FormikProps<any>;
    onFindAddress: (value: string, formValue: string) => Promise<void>;
    formattedAddressInformation: string | null;
    formattedTradingAddress: string | null;
    postalLabel: string;
}

const Step2AddressInformation: FC<Step2AddressInformation> = ({
    formik,
    onFindAddress,
    formattedAddressInformation,
    formattedTradingAddress,
    postalLabel,
}) => {
    const [sameAsRegisteredAddress, setSameAsRegisteredAddress] = useState(false);

    // Change event handler for the controlled checkbox
    const toggleChecked = (event) => {
        const { checked } = event.target;

        setSameAsRegisteredAddress(checked);

        if (checked) {
            formik.setValues((prev) => {
                return {
                    ...prev,
                    ai_ta_eircode: prev.ai_ra_eircode ?? '',
                    ai_ta_addressLine1: prev.ai_ra_addressLine1 ?? '',
                    ai_ta_addressLine2: prev.ai_ra_addressLine2 ?? '',
                    ai_ta_city: prev.ai_ra_city ?? '',
                    ai_ta_county: prev.ai_ra_county ?? '',
                    ai_ta_country: prev.ai_ra_country ?? 'Ireland',
                };
            });
        } else {
            formik.setValues((prev) => {
                return {
                    ...prev,
                    ai_ta_eircode: '',
                    ai_ta_addressLine1: '',
                    ai_ta_addressLine2: '',
                    ai_ta_city: '',
                    ai_ta_county: '',
                    ai_ta_country: 'Ireland',
                };
            });
        }
    };

    return (
        <Card heading="Address Information">
            {/* Registered Address */}
            <Box className="mt-20">
                <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                    {/* Row 1 */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item width="100%">
                                <Grid item xs={12} md={12}>
                                    <InputWithSearch
                                        searchText="Search"
                                        onSearch={(value) => {
                                            return onFindAddress(value, 'ai_ra');
                                        }}
                                        placeholder="Enter Search"
                                        label="Search"
                                        fieldName="registeredAddressSearch"
                                        maskedValue={formattedAddressInformation}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Row 2 */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="Address Line 1"
                                    name="ai_ra_addressLine1"
                                    placeholder="Address Line 1"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="Address Line 2"
                                    name="ai_ra_addressLine2"
                                    placeholder="Address Line 2"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Row 3 */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="Town/City"
                                    name="ai_ra_city"
                                    placeholder="Enter Town/City"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="County"
                                    name="ai_ra_county"
                                    placeholder="Enter County"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Row 4 */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <CountrySelectInput
                                    setFieldValue={formik.setFieldValue}
                                    fieldName="ai_ra_country"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label={postalLabel}
                                    name="ai_ra_eircode"
                                    placeholder={`Enter ${postalLabel}`}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Stack>
            </Box>

            {/* Check box */}
            <Grid
                container
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                marginTop="43px"
                marginBottom="33px"
            >
                <Typography fontSize="20px" color="#F18F34">
                    Trading Address
                </Typography>
                <Box>
                    <ControlledCheckBox
                        heading="Same as Registered Address"
                        value={sameAsRegisteredAddress}
                        name="sameAsRegisteredAddress"
                        label="Same as Registered Address"
                        showValue={false}
                        handleChange={toggleChecked}
                    />
                </Box>
            </Grid>

            {/* Trading Address */}
            <Box className="mt-20">
                <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                    {/* Row 5 */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item width="100%">
                                <Grid item xs={12} md={12}>
                                    <InputWithSearch
                                        searchText="Search"
                                        onSearch={(value) => {
                                            return onFindAddress(value, 'ai_ta');
                                        }}
                                        placeholder="Enter Search"
                                        label="Search"
                                        fieldName="tradingAddressSearch"
                                        maskedValue={formattedTradingAddress}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Row 6 */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="Address Line 1"
                                    name="ai_ta_addressLine1"
                                    placeholder="Address Line 1"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="Address Line 2"
                                    name="ai_ta_addressLine2"
                                    placeholder="Address Line 2"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Row 7 */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="Town/City"
                                    name="ai_ta_city"
                                    placeholder="Enter Town/City"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="County"
                                    name="ai_ta_county"
                                    placeholder="Enter County"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Row 8 */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <CountrySelectInput
                                    setFieldValue={formik.setFieldValue}
                                    fieldName="ai_ta_country"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label={postalLabel}
                                    name="ai_ta_eircode"
                                    placeholder={`Enter ${postalLabel}`}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Stack>
            </Box>
        </Card>
    );
};

export default Step2AddressInformation;
